import _ from 'lodash';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  FmlxButton,
  FmlxCheckBox,
  FmlxIcon,
  FmlxLabel,
  FmlxModal,
  FmlxRadioButton,
  FmlxSwitch,
  FmlxTextBox,
} from 'fmlx-common-ui';
import {ConsoleLog} from '../../../lib/ConsoleHelper';
import {
  FMLX_BUTTON,
  FMLX_CHECK_BOX,
  FMLX_COMPONENT,
  FMLX_MODAL,
  FMLX_RADIO_BUTTON,
  FMLX_SWITCH,
  FMLX_TEXT_INPUT_BOX,
} from '../../../const/FmlxComponentEnum';
import QuestionPosition from './QuestionPosition';
import {EQuestionByID, QUESTION_TYPE} from '../../../const/dataEnum';
import PropTypes from 'prop-types';

const CHANGE_TYPE_QUESTION = {
  question: 'question',
  description: 'description',
  questionRequired: 'required',
  questionType: 'type',
  questionScale: 'scale',
  weight: 'weight',
  commentsRequired: 'commentsRequired',
  self: 'Self',
  peer: 'Peer',
  manager: 'Manager',
  minimumofWords: 0,
};

const NewQuestion = (props) => {
  const {
    index,
    question,
    handleSaveNewQuestion,
    removeQuestion,
    changeQuestionPosition,
    onEditQuestion,
    onCloseQuestion,
    skill,
  } = props;
  const inputRef = useRef(null);

  const [newQuestion, setNewQuestion] = useState(question);
  const [questionRequired, setQuestionRequired] = useState(question?.required ? question?.required : false);
  const [commentRequired, setCommentRequired] = useState(
    question?.commentsRequired ? question?.commentsRequired : false
  );
  const [questionTitle, setQuestionTitle] = useState(skill ? question?.name : question?.question);
  const [questionDescription, setQuestionDescription] = useState(question?.description ? question?.description : '');
  const [questionType, setQuestionType] = useState(0);
  const [questionScale, setQuestionScale] = useState(0);
  const [questionWeight, setQuestionWeight] = useState(question?.weight ? question?.weight : 1);
  const [minimumofWords, setMinimumofWords] = useState(question?.minimumofWords ? question?.minimumofWords : 0);
  const [roleStates, setRoleStates] = useState([
    {name: 'Self', checked: question?.forSelf ? true : false},
    {name: 'Peer', checked: question?.forPeer ? true : false},
    {name: 'Manager', checked: question?.forManager ? true : false},
  ]);
  const [action, setAction] = useState('');
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  const REQUIRED_FIELDS = {
    [CHANGE_TYPE_QUESTION.commentsRequired]: (value) => setCommentRequired(value),
    [CHANGE_TYPE_QUESTION.questionRequired]: (value) => setQuestionRequired(value),
  };

  const typeItems = [
    {
      content: 'Linear Scale',
      value: 'linear-scale',
      hint: '',
      type: FMLX_RADIO_BUTTON.HINT_TYPE.INFORMATION,
      disabled: false,
    },
    {
      content: 'Essay',
      value: QUESTION_TYPE.ESSAY,
      hint: '',
      type: FMLX_RADIO_BUTTON.HINT_TYPE.INFORMATION,
      disabled: false,
    },
  ];
  const scaleItems = [
    {
      content: '1-5',
      value: QUESTION_TYPE.SCORE_5,
      hint: '',
      type: FMLX_RADIO_BUTTON.HINT_TYPE.INFORMATION,
      disabled: false,
    },
    {
      content: '1-10',
      value: QUESTION_TYPE.SCORE_10,
      hint: '',
      type: FMLX_RADIO_BUTTON.HINT_TYPE.INFORMATION,
      disabled: false,
    },
    {
      content: '1-20',
      value: QUESTION_TYPE.SCORE_20,
      hint: '',
      type: FMLX_RADIO_BUTTON.HINT_TYPE.INFORMATION,
      disabled: false,
    },
  ];

  const setDropdownState = useCallback(() => {
    if (skill) {
      if (question?.skillType === QUESTION_TYPE.ESSAY) {
        setQuestionType(EQuestionByID.ESSAY_QUESTION);
      } else if (
        question?.skillType === QUESTION_TYPE.SCORE_5 ||
        question?.skillType === QUESTION_TYPE.SCORE_10 ||
        question?.skillType === QUESTION_TYPE.SCORE_20
      ) {
        setQuestionType(EQuestionByID.LINIER_QUESTION);
        setQuestionScale(
          question?.skillType === QUESTION_TYPE.SCORE_5
            ? EQuestionByID.LINIER_ID
            : question?.skillType === QUESTION_TYPE.SCORE_10
            ? EQuestionByID.ESSAY_QUESTION
            : EQuestionByID.REQUIRED_QUESTION
        );
      }
    } else {
      if (question?.questionType === QUESTION_TYPE.ESSAY) {
        setQuestionType(EQuestionByID.ESSAY_QUESTION);
      } else if (
        question?.questionType === QUESTION_TYPE.SCORE_5 ||
        question?.questionType === QUESTION_TYPE.SCORE_10 ||
        question?.questionType === QUESTION_TYPE.SCORE_20
      ) {
        setQuestionType(EQuestionByID.LINIER_QUESTION);
        setQuestionScale(
          question?.questionType === QUESTION_TYPE.SCORE_5
            ? EQuestionByID.LINIER_QUESTION
            : question?.questionType === QUESTION_TYPE.SCORE_10
            ? EQuestionByID.ESSAY_QUESTION
            : EQuestionByID.REQUIRED_QUESTION
        );
      }
    }
  }, [skill, question]);

  useEffect(() => {
    setDropdownState();
  }, [setDropdownState]);

  const handleNewQuestionInput = (type, value) => {
    let tempNewQuestion = newQuestion;
    switch (type) {
      case CHANGE_TYPE_QUESTION.question:
        if (skill) tempNewQuestion.name = value;
        else tempNewQuestion.question = value;
        break;
      case CHANGE_TYPE_QUESTION.description:
        tempNewQuestion.description = value;
        break;
      case CHANGE_TYPE_QUESTION.questionRequired:
        tempNewQuestion.required = value;
        break;
      case CHANGE_TYPE_QUESTION.questionType:
        let tempType = value?.value === QUESTION_TYPE.ESSAY ? value?.value : scaleItems[questionScale].value;
        if (skill) tempNewQuestion.skillType = tempType;
        else tempNewQuestion.questionType = tempType;

        if (tempType === QUESTION_TYPE.ESSAY) {
          setCommentRequired(true);
          tempNewQuestion.commentsRequired = true;
        }
        break;
      case CHANGE_TYPE_QUESTION.questionScale:
        if (skill) tempNewQuestion.skillType = value?.value;
        else tempNewQuestion.questionType = value?.value;
        break;
      case CHANGE_TYPE_QUESTION.weight:
        tempNewQuestion.weight = Number(value);
        break;
      case CHANGE_TYPE_QUESTION.minimumofWords:
        const sanitizedMinWord = Math.max(Number(value), 1);
        tempNewQuestion.minimumofWords = sanitizedMinWord;
        setMinimumofWords(sanitizedMinWord);
        break;
      case CHANGE_TYPE_QUESTION.commentsRequired:
        tempNewQuestion.commentsRequired = value;
        const currentType = skill ? tempNewQuestion.skillType : tempNewQuestion.questionType;

        if (currentType === QUESTION_TYPE.ESSAY) {
          tempNewQuestion.required = false;
          setQuestionRequired(false);
        }
        break;
      case CHANGE_TYPE_QUESTION.self:
        tempNewQuestion.forSelf = value ? 0 : 1;
        break;
      case CHANGE_TYPE_QUESTION.peer:
        tempNewQuestion.forPeer = value ? 0 : 1;
        break;
      case CHANGE_TYPE_QUESTION.manager:
        tempNewQuestion.forManager = value ? 0 : 1;
        break;
      default:
        console.warn(`Unhandled question input type: ${type}`);
        break;
    }

    ConsoleLog('tempNewQuestion', tempNewQuestion);
    setNewQuestion(tempNewQuestion);
  };

  const handleRoleState = (type) => {
    let tempRoles = roleStates.map((role) =>
      role.name === type ? {name: role.name, checked: !role.checked} : _.cloneDeep(role)
    );
    setRoleStates(tempRoles);
  };

  const isSaveButtonDisabled = () => {
    const minWordValid = !commentRequired || minimumofWords >= 1;
    if (
      (newQuestion.forPeer === 0 && newQuestion.forManager === 0 && newQuestion.forSelf === 0) ||
      newQuestion.question === '' ||
      !minWordValid
    ) {
      return true;
    }
    return false;
  };

  const handleRemoveConfirmation = (dataQuestion) => {
    setAction('Delete');
    setSelectedQuestion(dataQuestion);
  };

  const handleDeleteQuestion = (event) => {
    event.preventDefault();
    if (selectedQuestion) {
      removeQuestion(selectedQuestion);
    }
  };

  const handleCloseDeleteQuestion = () => {
    setAction('');
  };

  const deletePrompt = (
    <FmlxModal
      open={action === 'Delete'}
      title="Delete Question"
      size={FMLX_MODAL.SIZE.MD}
      onCloseClick={() => handleCloseDeleteQuestion()}
      primaryButton={{
        show: true,
        label: 'Delete',
        type: FMLX_BUTTON.TYPE.DANGER,
        variant: FMLX_BUTTON.VARIANT.CONTAIN,
        onClick: (e) => handleDeleteQuestion(e),
      }}
      secondaryButton={{
        show: false,
      }}
      tertiaryButton={{
        show: false,
      }}
    >
      <div className="mb-16px">Do you want to delete this question?</div>
    </FmlxModal>
  );

  const handleOnChangeMinWords = (value) => {
    setMinimumofWords(value);
  };

  const handleQuestionCommentRequired = (checked) => {
    setCommentRequired(checked);
    handleNewQuestionInput(CHANGE_TYPE_QUESTION.commentsRequired, checked);
  };

  const handleQuestionRequiredChange = (checked) => {
    setCommentRequired(checked);
    handleNewQuestionInput(CHANGE_TYPE_QUESTION.commentsRequired, checked);

    setQuestionRequired(checked);
    handleNewQuestionInput(CHANGE_TYPE_QUESTION.questionRequired, checked);

    if (!checked) {
      setCommentRequired(false);
      handleNewQuestionInput(CHANGE_TYPE_QUESTION.commentsRequired, false);
    }
  };

  return (
    <>
      <div onClick={(e) => e.stopPropagation()} className="questions_wrapper">
        <div className="heading">
          <div id="question-title-div" className="field-div">
            <FmlxLabel label="Title" required={false} />
            <FmlxTextBox
              // label="Question Title"
              placeholder="Type questions title..."
              mode={FMLX_TEXT_INPUT_BOX.MODE.TEXT}
              size={FMLX_TEXT_INPUT_BOX.SIZE.MEDIUM}
              variant={FMLX_TEXT_INPUT_BOX.VARIANT.OUTLINE}
              decoration={FMLX_TEXT_INPUT_BOX.DECORATION.COUNTER}
              maxLength={100}
              disabled={false}
              autoFocus={true}
              onChange={({value, minLength, maxLength}) => {
                setQuestionTitle(value);
              }}
              onBlur={({value, minLength, maxLength}) => {
                handleNewQuestionInput(CHANGE_TYPE_QUESTION.question, value);
              }}
              style={{width: '200px'}}
              ref={inputRef}
              value={questionTitle}
            />
          </div>
          <div id="question-required-div" className="field-div">
            <FmlxLabel label="Required" required={false} />
            <FmlxSwitch
              // label={"Required"}
              size={FMLX_SWITCH.SIZE.M}
              // labelPlacement={FMLX_SWITCH.PLACEMENT.TOP}
              withIcon={false}
              checked={questionRequired}
              disabled={false}
              onChange={(checked) => handleQuestionRequiredChange(checked)}
            />
          </div>
          <div id="question-type-div" className="field-div">
            <FmlxLabel label="Type" required={false} />
            <FmlxRadioButton
              // title={"Type"}
              selectedIndex={questionType}
              size={FMLX_RADIO_BUTTON.SISE.MEDIUM}
              items={typeItems}
              alignment={FMLX_COMPONENT.ALIGNMENT.LEFT}
              direction={FMLX_COMPONENT.DIRECTION.ROW}
              // labelPlacement={FMLX_RADIO_BUTTON.PLACEMENT.TOP}
              fullWidth={true}
              disabled={false}
              onChange={({index}) => {
                setQuestionType(index);
                handleNewQuestionInput(CHANGE_TYPE_QUESTION.questionType, typeItems[index]);
              }}
            />
          </div>
          <div id="question-category-div" className="field-div">
            {roleStates.map((role, index) => (
              <React.Fragment key={index}>
                <FmlxCheckBox
                  label={role.name}
                  labelPlacement={FMLX_CHECK_BOX.LABEL_PLACEMENT.RIGHT}
                  disabled={false}
                  size={FMLX_CHECK_BOX.SIZE.M}
                  checked={role.checked}
                  onChange={() => {
                    handleRoleState(role.name);
                    handleNewQuestionInput(role.name, role.checked);
                  }}
                />
              </React.Fragment>
            ))}
          </div>
          {questionType === EQuestionByID.ESSAY_QUESTION && questionRequired && (
            <div className="required-attribute-container">
              {/* <div id="comment-required-div" className="field-div">
                <FmlxLabel label="Comment Required" required={false}/>
                <FmlxSwitch
                  // label={"Comment Required"}
                  size={FMLX_SWITCH.SIZE.M}
                  // labelPlacement={FMLX_SWITCH.PLACEMENT.TOP}
                  withIcon={false}
                  checked={commentRequired}
                  disabled={false}
                  onChange={handleQuestionCommentRequired}
                />
              </div> */}
              <div id="comment-min-words-comment-div" className="field-div">
                <FmlxLabel label="Min Word Comment" required={false} />
                <FmlxTextBox
                  mode={FMLX_TEXT_INPUT_BOX.MODE.NUMBER}
                  placeholder="0"
                  value={minimumofWords}
                  onChange={({value}) => {
                    handleOnChangeMinWords(value);
                  }}
                  onBlur={({value}) => {
                    handleNewQuestionInput(CHANGE_TYPE_QUESTION.minimumofWords, value);
                  }}
                  disabled={!commentRequired}
                  minValue={0}
                />
              </div>
            </div>
          )}

          {questionType === EQuestionByID.LINIER_QUESTION && (
            <>
              <div id="question-weight-div" className="field-div">
                <FmlxLabel label="Weight" required={false} />
                <FmlxTextBox
                  mode={FMLX_TEXT_INPUT_BOX.MODE.NUMBER}
                  placeholder="1"
                  value={questionWeight}
                  onChange={({value}) => {
                    setQuestionWeight(value);
                  }}
                  onBlur={({value}) => {
                    handleNewQuestionInput(CHANGE_TYPE_QUESTION.weight, value);
                  }}
                />
              </div>
              {questionRequired ? (
                <>
                  <div id="comment-required-div" className="field-div">
                    <FmlxLabel label="Comment Required" required={false} />
                    <FmlxSwitch
                      // label={"Comment Required"}
                      size={FMLX_SWITCH.SIZE.M}
                      // labelPlacement={FMLX_SWITCH.PLACEMENT.TOP}
                      withIcon={false}
                      checked={commentRequired}
                      disabled={false}
                      onChange={(checked) => handleQuestionCommentRequired(checked)}
                    />
                  </div>
                  <div id="comment-min-words-comment-div" className="field-div">
                    <FmlxLabel label="Min Word Comment" required={false} />
                    <FmlxTextBox
                      mode={FMLX_TEXT_INPUT_BOX.MODE.NUMBER}
                      placeholder="0"
                      value={minimumofWords}
                      onChange={({value}) => {
                        handleOnChangeMinWords(value);
                      }}
                      onBlur={({value}) => {
                        handleNewQuestionInput(CHANGE_TYPE_QUESTION.minimumofWords, value);
                      }}
                      disabled={!commentRequired}
                      minValue={0}
                    />
                  </div>
                </>
              ) : (
                <></>
              )}
              <div id="question-scale-div" className="field-div">
                <FmlxLabel label="Scale" required={false} />
                <FmlxRadioButton
                  // title={"Type"}
                  selectedIndex={questionScale}
                  size={FMLX_RADIO_BUTTON.SISE.MEDIUM}
                  items={scaleItems}
                  alignment={FMLX_COMPONENT.ALIGNMENT.LEFT}
                  direction={FMLX_COMPONENT.DIRECTION.ROW}
                  // labelPlacement={FMLX_RADIO_BUTTON.PLACEMENT.TOP}
                  fullWidth={true}
                  disabled={false}
                  onChange={({index}) => {
                    setQuestionScale(index);
                    handleNewQuestionInput(CHANGE_TYPE_QUESTION.questionScale, scaleItems[index]);
                  }}
                />
              </div>
            </>
          )}
        </div>
        <div style={{marginTop: '16px'}}>
          <FmlxLabel label="Descriptions" required={false} />
          <FmlxTextBox
            // label="Question Descriptions"
            placeholder="Specify questions descriptions here..."
            mode={FMLX_TEXT_INPUT_BOX.MODE.TEXTAREA}
            size={FMLX_TEXT_INPUT_BOX.SIZE.MEDIUM}
            variant={FMLX_TEXT_INPUT_BOX.VARIANT.OUTLINE}
            decoration={FMLX_TEXT_INPUT_BOX.DECORATION.COUNTER}
            maxLength={2048}
            disabled={false}
            autoFocus={true}
            // inlineText={""}
            onChange={({value, minLength, maxLength}) => {
              setQuestionDescription(value);
            }}
            onBlur={({value, minLength, maxLength}) => {
              handleNewQuestionInput(CHANGE_TYPE_QUESTION.description, value);
            }}
            style={{width: '200px'}}
            ref={inputRef}
            value={questionDescription}
          />
        </div>
        <div className="newquestion--action-button-div">
          <FmlxButton
            size={FMLX_COMPONENT.SIZE.XL}
            type={FMLX_BUTTON.TYPE.DANGER}
            variant={FMLX_BUTTON.VARIANT.OUTLINE}
            disabled={false}
            label="DELETE"
            onClick={() => handleRemoveConfirmation(question)}
            style={{cursor: 'pointer'}}
            onlyIcon={false}
            icon={<FmlxIcon name={'Trash'} />}
            withTooltip={false}
            tooltipPlacement={'top'}
            tooltipDescription={'Delete Question'}
          ></FmlxButton>
          <FmlxButton
            size={FMLX_COMPONENT.SIZE.XL}
            type={FMLX_BUTTON.TYPE.PRIMARY}
            variant={FMLX_BUTTON.VARIANT.OUTLINE}
            disabled={false}
            label="CANCEL"
            onClick={() => {
              onCloseQuestion();
            }}
            style={{cursor: 'pointer'}}
            onlyIcon={false}
            icon={<FmlxIcon name={'CloseOutline'} />}
            withTooltip={false}
            tooltipPlacement={'top'}
            tooltipDescription={'Cancel Question'}
          ></FmlxButton>
          <FmlxButton
            size={FMLX_COMPONENT.SIZE.XL}
            type={FMLX_BUTTON.TYPE.PRIMARY}
            variant={FMLX_BUTTON.VARIANT.CONTAIN}
            disabled={isSaveButtonDisabled()}
            label="SAVE"
            onClick={() => {
              if (newQuestion.isNew) handleSaveNewQuestion(newQuestion);
              else onEditQuestion(newQuestion);
            }}
            style={{cursor: 'pointer'}}
            onlyIcon={false}
            icon={<FmlxIcon name={'Save'} />}
            withTooltip={false}
            tooltipPlacement={'top'}
            tooltipDescription={'Save Question'}
          ></FmlxButton>
        </div>
        <QuestionPosition changeQuestionPosition={changeQuestionPosition} index={index} />
        {deletePrompt}
      </div>
      {/* </ClickAwayListener> */}
    </>
  );
};

NewQuestion.propTypes = {
  index: PropTypes.number,
  question: PropTypes.object,
  handleSaveNewQuestion: PropTypes.func,
  removeQuestion: PropTypes.func,
  changeQuestionPosition: PropTypes.func,
  onEditQuestion: PropTypes.func,
  onCloseQuestion: PropTypes.func,
  skill: PropTypes.bool,
};

export default NewQuestion;
